import { keyframes } from '@emotion/react'
import styled from '@emotion/styled'
import { ApiClient, ApiShopperLandingPageLink } from '@storefronts/common-types'
import { MessageCircle } from 'lucide-react'
import {
    FormEvent,
    ReactNode,
    RefObject,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import {
    ACCENT_FILL_COLOR_VAR,
    ACCENT_TEXT_COLOR_VAR,
    BUTTON_BORDER_RADIUS_VAR,
    PRIMARY_CTA_FILL_COLOR_VAR,
    PRIMARY_CTA_TEXT_COLOR_VAR,
} from '../../context/CustomizationContextProvider'
import { BrandChatMessageInput } from '../../analytics/tracking'
import { AnalyticsContext } from '../../context/analyticsContext'
import { CartContext } from '../../context/cartContext'
import { ViddyLPContext } from '../../context/viddyLPContext'
import { BRAND_CHAT_FONT } from './brandChatStyles'
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock'
import { useEscapeToDismiss } from '../../hooks/useEscapeToDismiss'
import { useInView } from '../../hooks/useInView'
import { ScrollingLayout } from '../../style/scrollingLayout'
import { BrandAvatar, brandName } from './BrandAvatar'
import { BrandChatComparison } from './BrandChatComparison'
import { BrandChatContext, useSellingBrief } from './brandChatContext'
import { BrandChatHeroCard } from './BrandChatHeroCard'
import { BrandChatProductBar } from './BrandChatProductBar'
import { BrandChatProductCard } from './BrandChatProductCard'
import { useSheetPosition, useSwipeToDismiss } from './sheetHooks'
import { BrandChatEntry, useBrandChat } from './useBrandChat'

const PANEL_WIDTH = 440

/**
 * Shopper-facing brand chat. A bubble opens a bottom sheet on phones and a
 * side panel on desktop; the page behind stays usable in both. The page under
 * it can open the chat with a question through BrandChatContext. It always
 * wraps the page, so turning it on after hydration doesn't remount the page.
 */
export const BrandChat: React.FC<{
    zIndex: number
    enabled: boolean
    children: ReactNode
}> = ({ zIndex, enabled, children }) => {
    const { landingPage } = useContext(ViddyLPContext)
    const { Analytics } = useContext(AnalyticsContext)
    const [open, setOpen] = useState(false)
    const chat = useBrandChat(landingPage.landingPageHash)
    const brief = useSellingBrief(landingPage.landingPageHash, enabled)
    const close = useCallback(() => setOpen(false), [])
    const { send } = chat

    useEscapeToDismiss(open, close)

    const controls = useMemo(
        () =>
            enabled
                ? {
                      brief,
                      ask: (question: string, input: BrandChatMessageInput) => {
                          setOpen(true)
                          send(question)
                          Analytics.TRACK_BRAND_CHAT_OPEN()
                          Analytics.TRACK_BRAND_CHAT_MESSAGE_SEND(input)
                      },
                  }
                : null,
        [enabled, brief, send, Analytics],
    )

    return (
        <BrandChatContext.Provider value={controls}>
            {children}
            {!enabled ? null : open ? (
                <ChatPanel
                    client={landingPage.client}
                    chat={chat}
                    onClose={close}
                    zIndex={zIndex}
                />
            ) : (
                <Bubble
                    type="button"
                    style={{ zIndex }}
                    aria-label={`Chat with ${brandName(landingPage.client)}`}
                    onClick={() => {
                        setOpen(true)
                        chat.start()
                        Analytics.TRACK_BRAND_CHAT_OPEN()
                    }}
                >
                    <BrandAvatar client={landingPage.client} size={56} />
                    <BubbleBadge aria-hidden>
                        <MessageCircle size={14} strokeWidth={2.5} />
                    </BubbleBadge>
                </Bubble>
            )}
        </BrandChatContext.Provider>
    )
}

const ChatPanel: React.FC<{
    client: ApiClient
    chat: ReturnType<typeof useBrandChat>
    onClose: () => void
    zIndex: number
}> = ({ client, chat, onClose, zIndex }) => {
    const { entries, pending, progress, failed, send, retry } = chat
    const { Analytics } = useContext(AnalyticsContext)
    const [draft, setDraft] = useState('')
    const position = useSheetPosition()
    const { offset, handleProps } = useSwipeToDismiss(onClose)
    const thread = useRef<HTMLDivElement>(null)
    useBodyScrollLock(true)

    // A reply scrolls to its first line so its card reads top-down; a sent
    // message follows the bottom.
    useEffect(() => {
        const element = thread.current
        if (!element) return
        const anchor =
            !pending && entries.at(-1)?.role === 'brand'
                ? element.querySelector<HTMLElement>(
                      `[data-index="${entries.length - 1}"]`,
                  )
                : null
        element.scrollTo({
            top: anchor ? anchor.offsetTop - 12 : element.scrollHeight,
            behavior: 'smooth',
        })
    }, [entries, pending])

    const focusCardRef = useRef<HTMLDivElement>(null)
    const focusIndex = entries.findLastIndex((_, index) =>
        isNewCard(entries, index),
    )
    const focus = entries[focusIndex]
    const focusLink =
        focus?.role === 'brand' && focus.reply.card
            ? focus.links.get(focus.reply.card.productId)
            : undefined

    const lastEntry = entries.at(-1)
    const suggestions =
        !pending && lastEntry?.role === 'brand'
            ? lastEntry.reply.suggestedReplies
            : []
    const name = brandName(client)

    const submit = (event: FormEvent) => {
        event.preventDefault()
        send(draft)
        Analytics.TRACK_BRAND_CHAT_MESSAGE_SEND('typed')
        setDraft('')
    }

    return (
        <Backdrop style={{ zIndex }} onClick={onClose}>
            <Panel
                role="dialog"
                aria-label={`Chat with ${name}`}
                onClick={(event) => event.stopPropagation()}
                style={{
                    top: position?.top,
                    bottom: position ? 'auto' : undefined,
                    height: position?.height,
                    transform: offset ? `translateY(${offset}px)` : undefined,
                    transition: offset ? 'none' : undefined,
                }}
            >
                <DragArea {...handleProps}>
                    <Grabber />
                    <Header>
                        <BrandAvatar client={client} size={32} />
                        <HeaderText>
                            <HeaderTitle>{name}</HeaderTitle>
                            <HeaderSubtitle>AI agent</HeaderSubtitle>
                        </HeaderText>
                        <CloseButton
                            type="button"
                            aria-label="Close chat"
                            onClick={onClose}
                        >
                            ×
                        </CloseButton>
                    </Header>
                </DragArea>
                <Thread ref={thread}>
                    {entries.map((entry, index) => (
                        <Entry
                            key={index}
                            index={index}
                            entry={entry}
                            showCard={isNewCard(entries, index)}
                            cardRef={
                                index === focusIndex ? focusCardRef : undefined
                            }
                        />
                    ))}
                    {suggestions.length > 0 && (
                        <Suggestions>
                            {suggestions.map((suggestion) => (
                                <Suggestion
                                    key={suggestion}
                                    type="button"
                                    onClick={() => {
                                        send(suggestion)
                                        Analytics.TRACK_BRAND_CHAT_MESSAGE_SEND(
                                            'suggestion',
                                        )
                                    }}
                                >
                                    {suggestion}
                                </Suggestion>
                            ))}
                        </Suggestions>
                    )}
                    {pending && (
                        <Working aria-live="polite">
                            <ThinkingDots aria-hidden>
                                <span />
                                <span />
                                <span />
                            </ThinkingDots>
                            {progress && <WorkingText>{progress}</WorkingText>}
                        </Working>
                    )}
                    {failed && (
                        <ErrorLine>
                            Something went wrong.{' '}
                            <button type="button" onClick={retry}>
                                Try again
                            </button>
                        </ErrorLine>
                    )}
                </Thread>
                <Footer>
                    {focusLink && (
                        <OffscreenProductBar
                            key={focusIndex}
                            cardRef={focusCardRef}
                            link={focusLink}
                        />
                    )}
                    <CheckoutBar />
                    <Composer onSubmit={submit}>
                        <Input
                            value={draft}
                            onChange={(event) => setDraft(event.target.value)}
                            placeholder={`Message ${name}`}
                            aria-label="Message"
                            enterKeyHint="send"
                        />
                        <SendButton
                            type="submit"
                            aria-label="Send"
                            disabled={!draft.trim() || pending}
                        >
                            ↑
                        </SendButton>
                    </Composer>
                    <PoweredBy>
                        Brand agent and storefront by Black Crow
                    </PoweredBy>
                </Footer>
            </Panel>
        </Backdrop>
    )
}

// The product the chat is about stays one tap from the cart while its big
// card is scrolled out of view.
const OffscreenProductBar: React.FC<{
    cardRef: RefObject<HTMLDivElement>
    link: ApiShopperLandingPageLink
}> = ({ cardRef, link }) => {
    const cardInView = useInView(cardRef, { initialInView: true })
    return cardInView ? null : <BrandChatProductBar link={link} />
}

// The big card only when the pick changed; the same product again adds nothing.
const isNewCard = (entries: BrandChatEntry[], index: number): boolean => {
    const entry = entries[index]
    if (entry.role !== 'brand' || !entry.reply.card) return false
    const previous = entries
        .slice(0, index)
        .reverse()
        .find((earlier) => earlier.role === 'brand' && earlier.reply.card)
    return (
        previous?.role !== 'brand' ||
        previous.reply.card?.productId !== entry.reply.card.productId
    )
}

const Entry: React.FC<{
    entry: BrandChatEntry
    index: number
    showCard: boolean
    cardRef?: RefObject<HTMLDivElement>
}> = ({ entry, index, showCard, cardRef }) => {
    if (entry.role === 'shopper') {
        return <ShopperMessage data-index={index}>{entry.text}</ShopperMessage>
    }
    const { reply, links } = entry
    const heroLink = reply.card ? links.get(reply.card.productId) : undefined
    const cards = reply.productIds.flatMap((productId) => {
        const link = links.get(productId)
        return link ? [link] : []
    })
    return (
        <BrandTurn data-index={index}>
            <BrandMessage>
                {paragraphs(reply.text).map((paragraph, at) => (
                    <p key={at}>{paragraph}</p>
                ))}
            </BrandMessage>
            {showCard && reply.card && heroLink && (
                <BrandChatHeroCard
                    cardRef={cardRef}
                    card={reply.card}
                    link={heroLink}
                />
            )}
            {cards.length > 0 && (
                <Cards>
                    {cards.map((link) => (
                        <BrandChatProductCard key={link.linkHash} link={link} />
                    ))}
                </Cards>
            )}
            {reply.comparison && (
                <BrandChatComparison
                    comparison={reply.comparison}
                    links={links}
                />
            )}
        </BrandTurn>
    )
}

const paragraphs = (text: string) =>
    text
        .split(/\n{2,}/)
        .map((paragraph) => paragraph.trim())
        .filter(Boolean)

// Buy now for the whole chat: once something is in the cart, checkout is one tap away.
const CheckoutBar: React.FC = () => {
    const { cart, onCheckout } = useContext(CartContext)
    const { Analytics } = useContext(AnalyticsContext)
    const count = cart.items.reduce((sum, item) => sum + item.quantity, 0)
    if (count === 0) return null
    return (
        <CheckoutButton
            type="button"
            onClick={() => {
                Analytics.TRACK_CART_CHECKOUT_CLICK({ cart })
                onCheckout()
            }}
        >
            Checkout · {count} {count === 1 ? 'item' : 'items'}
        </CheckoutButton>
    )
}

const Bubble = styled.button`
    position: fixed;
    right: 16px;
    /* Clears the sticky footer CTA on phones. */
    bottom: calc(96px + env(safe-area-inset-bottom));
    appearance: none;
    border: 0;
    padding: 0;
    border-radius: 50%;
    background: none;
    cursor: pointer;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.18);

    ${ScrollingLayout.DESKTOP_MEDIA} {
        right: 24px;
        bottom: 24px;
    }
`

const BubbleBadge = styled.span`
    position: absolute;
    right: -2px;
    bottom: -2px;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(${PRIMARY_CTA_FILL_COLOR_VAR}, #0d0d0d);
    color: var(${PRIMARY_CTA_TEXT_COLOR_VAR}, #ffffff);
    box-shadow: 0 0 0 2px #ffffff;
`

const slideUp = keyframes`
    from { transform: translateY(24px); opacity: 0; }
    to { transform: none; opacity: 1; }
`

const Backdrop = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
`

const Panel = styled.div`
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    height: calc(100dvh - 12px);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    border-radius: 22px 22px 0 0;
    background: #ffffff;
    color: #0d0d0d;
    font-family: ${BRAND_CHAT_FONT};
    -webkit-font-smoothing: antialiased;
    animation: ${slideUp} 320ms cubic-bezier(0.2, 0.8, 0.2, 1) both;

    @media (prefers-reduced-motion: reduce) {
        animation: none;
    }

    /* Docked full height on the right, the page dimmed behind it. */
    ${ScrollingLayout.DESKTOP_MEDIA} {
        top: 0;
        left: auto;
        width: ${PANEL_WIDTH}px;
        height: auto;
        border-radius: 0;
    }
`

const DragArea = styled.div`
    touch-action: none;
    flex: none;
    display: flex;
    flex-direction: column;
`

const Grabber = styled.div`
    align-self: center;
    width: 36px;
    height: 5px;
    border-radius: 999px;
    background: #d1d1d6;
    margin: 6px 0 0;

    ${ScrollingLayout.DESKTOP_MEDIA} {
        display: none;
    }
`

const Header = styled.header`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 6px 12px 8px 16px;
    border-bottom: 1px solid #ececec;
`

const HeaderText = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 1px;
`

const HeaderTitle = styled.div`
    font-weight: 600;
    font-size: 15px;
`

const HeaderSubtitle = styled.div`
    font-size: 12px;
    color: #8e8e93;
`

const CloseButton = styled.button`
    appearance: none;
    border: 0;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: #f1f1f1;
    font-size: 20px;
    line-height: 1;
    color: #48484a;
    cursor: pointer;
`

const Thread = styled.div`
    position: relative;
    flex: 1;
    overflow-y: auto;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
    padding: 12px 14px;
    display: flex;
    flex-direction: column;
    gap: 10px;

    & > * {
        flex-shrink: 0;
    }
`

const BrandTurn = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`

const BrandMessage = styled.div`
    max-width: 92%;
    font-size: 15px;
    line-height: 1.5;

    p {
        margin: 0 0 10px;
    }

    p:last-child {
        margin-bottom: 0;
    }
`

const ShopperMessage = styled.p`
    margin: 0;
    align-self: flex-end;
    max-width: 78%;
    padding: 10px 14px;
    border-radius: 20px;
    font-size: 15px;
    line-height: 1.5;
    background: #f1f1f1;
    color: #0d0d0d;
`

const pulse = keyframes`
    0%, 80%, 100% { opacity: 0.25; transform: translateY(0); }
    40% { opacity: 1; transform: translateY(-2px); }
`

const ThinkingDots = styled.span`
    display: inline-flex;
    gap: 5px;
    padding: 8px 0;

    span {
        width: 7px;
        height: 7px;
        border-radius: 50%;
        background: #6b7280;
        animation: ${pulse} 1.2s infinite ease-in-out;
    }

    span:nth-of-type(2) {
        animation-delay: 0.15s;
    }

    span:nth-of-type(3) {
        animation-delay: 0.3s;
    }
`

const Working = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`

const WorkingText = styled.span`
    font-size: 13px;
    color: #6e6e73;
`

const ErrorLine = styled.p`
    margin: 0;
    font-size: 13px;
    color: #b91c1c;

    button {
        appearance: none;
        border: 0;
        padding: 0;
        background: none;
        font: inherit;
        color: inherit;
        text-decoration: underline;
        cursor: pointer;
    }
`

const Footer = styled.footer`
    flex: none;
    border-top: 1px solid #ececec;
    padding: 8px 14px calc(6px + env(safe-area-inset-bottom));
    display: flex;
    flex-direction: column;
    gap: 6px;
`

/* One swipeable row; the faded edge shows there's more. */
const Suggestions = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`

const Suggestion = styled.button`
    appearance: none;
    font: inherit;
    font-size: 14px;
    line-height: 1.3;
    text-align: left;
    padding: 9px 14px;
    border-radius: 18px;
    border: 1px solid #e3e3e3;
    background: #ffffff;
    color: #0d0d0d;
    cursor: pointer;
`

const Composer = styled.form`
    display: flex;
    gap: 8px;
    align-items: center;
`

const Input = styled.input`
    flex: 1;
    min-width: 0;
    border: 1px solid #e3e3e3;
    border-radius: 999px;
    padding: 10px 14px;
    font: inherit;
    /* 16px keeps iOS from zooming the page on focus. */
    font-size: 16px;
    background: #ffffff;
    color: #0d0d0d;
    outline: none;

    &:focus {
        border-color: #8e8e93;
    }
`

const SendButton = styled.button`
    flex: none;
    width: 38px;
    height: 38px;
    border-radius: 50%;
    border: 0;
    background: var(${ACCENT_FILL_COLOR_VAR}, #0d0d0d);
    color: var(${ACCENT_TEXT_COLOR_VAR}, #ffffff);
    font-size: 18px;
    cursor: pointer;

    &:disabled {
        background: #d1d1d6;
        color: #ffffff;
        cursor: default;
    }
`

const PoweredBy = styled.div`
    text-align: center;
    font-size: 11px;
    color: #aeaeb2;
`

const CheckoutButton = styled.button`
    appearance: none;
    width: 100%;
    font-family: ${BRAND_CHAT_FONT};
    text-transform: none;
    font-size: 15px;
    font-weight: 600;
    padding: 12px 16px;
    border-radius: var(${BUTTON_BORDER_RADIUS_VAR}, 999px);
    border: 0;
    background: var(${PRIMARY_CTA_FILL_COLOR_VAR}, #0d0d0d);
    color: var(${PRIMARY_CTA_TEXT_COLOR_VAR}, #ffffff);
    cursor: pointer;
`

const Cards = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`
